import React, { Suspense, useEffect, useRef, useState } from 'react';

import { LoginModal, Props as LoginModalProps } from './LoginModal';
import {
  ModalPage,
  PartialClickTrackingOptions,
  PartialConfigurableText,
  RegistrationParams,
} from './types';
import {
  lookupModalVariant,
  mergeConfiguredText,
} from './modalVariantRegistry';
import { abTest as bucketLoswpModalSmokeTest } from 'js/ab_tests/tests/loswp_modal_smoke_test';
import { abTest as bucketLoswpModalAutotest } from 'js/ab_tests/tests/loswp_modal_autotest_2026_q2';
import Cookies from 'js-cookie';
import routes from 'js/core_pre_webpack/routes';

type LoginModalEventDetail = {
  postLoginRedirectUrl?: string;
  forcePostLoginRedirectUrl?: boolean;
  clickTrackingContext?: PartialClickTrackingOptions;
  configuredText?: PartialConfigurableText;
  registrationParams: RegistrationParams;
  initialPage?: ModalPage;
};

// # Usage
//
// It isn't necessary to use openLoginModal directly. The LoginModalManager will listen for
// the 'open-login-modal' event and open the modal when it is triggered. Example:
//
// document.dispatchEvent(new CustomEvent('open-login-modal', {
//   detail: {
//     postLoginRedirectUrl: '/dashboard',
//     configuredText: {
//       buttonsPage: {
//         header: 'Custom welcome message'
//       }
//     },
//     registrationParams: {
//       user_source: 'header_button'
//     }
//   }
// }));
//
export const openLoginModal = (detail: LoginModalEventDetail) => {
  document.dispatchEvent(new CustomEvent('open-login-modal', { detail }));
};

export const LoginModalManager: React.FC = () => {
  const [modalProps, setModalProps] = useState<LoginModalProps>({
    open: false,
    registrationParams: {
      user_source: 'unknown', // user_source is required in event so this should never actually be sent
    },
  });

  // Resolved once on mount from the loswp_modal_autotest_2026_q2 bucket;
  // drives variant lookup at modal-open time. Stays 'control' for every
  // visitor while the YAML is `active: false` (Phase 1, verify-bucketing).
  const modalVariantRef = useRef<string>('control');

  useEffect(() => {
    // Smoke test: bucketing emission only. The returned bucket is
    // unused; the value of this call is the server-side bucketing
    // write it triggers (validates that auvid → bucket math works in
    // production with Cloudflare full-page caching disabled).
    bucketLoswpModalSmokeTest();

    // Real test: returns 'control' for everyone while inactive; once
    // the YAML flips to active: true (Phase 3 activation), returns the
    // actual variant. Drives the fork dispatch and configuredText
    // override below.
    const variant = bucketLoswpModalAutotest();
    modalVariantRef.current = variant;

    // Trigger the bucketed fork's dynamic import now so the chunk
    // loads in the background, well before any CTA click. The browser
    // caches the module; React.lazy resolves from cache on render.
    const entry = lookupModalVariant(variant);
    void entry.forkLoader?.();
  }, []);

  useEffect(() => {
    const handleOpenLoginModal = (
      event: CustomEventInit<LoginModalEventDetail>,
    ) => {
      const { detail } = event;
      if (!detail) {
        throw new Error('No detail provided to open-login-modal event');
      }

      if (
        !document.querySelector<HTMLMetaElement>("[name='csrf-token']") &&
        !Cookies.get('masked_csrf_token')
      ) {
        window.location.href = routes.login_path({
          post_login_redirect_url: encodeURIComponent(window.location.href),
          modal_fallback: 'true',
        });
      } else {
        const variantEntry = lookupModalVariant(modalVariantRef.current);
        setModalProps(() => ({
          open: true,
          postLoginRedirectUrl: detail.postLoginRedirectUrl,
          forcePostLoginRedirectUrl: detail.forcePostLoginRedirectUrl,
          clickTrackingContext: detail.clickTrackingContext,
          configuredText: mergeConfiguredText(
            detail.configuredText,
            variantEntry.configuredText,
          ),
          registrationParams: detail.registrationParams,
          initialPage: detail.initialPage,
        }));
      }
    };

    document.addEventListener('open-login-modal', handleOpenLoginModal);

    return () => {
      document.removeEventListener('open-login-modal', handleOpenLoginModal);
    };
  }, []);

  const handleClose = () => {
    setModalProps((prev) => ({
      ...prev,
      open: false,
    }));
  };

  const variantEntry = lookupModalVariant(modalVariantRef.current);
  const ForkComponent = variantEntry.forkComponent;

  if (ForkComponent) {
    // Fallback is null (not the default modal) so a click before the fork
    // chunk arrives renders nothing momentarily rather than briefly
    // showing control and then swapping to the variant — which would
    // actively undermine the test signal for structural variants
    // (cluster F).
    return (
      <Suspense fallback={null}>
        <ForkComponent {...modalProps} onClose={handleClose} />
      </Suspense>
    );
  }

  return <LoginModal {...modalProps} onClose={handleClose} />;
};
